import type { SelectedLineRange } from "@/context/file"
import type { Prompt } from "@/context/prompt"
import type { PreviewCandidate } from "@/control-room/terminal-preview"
import { createSignal } from "solid-js"

type HandoffSession = {
  prompt?: string
  promptParts?: Prompt
  files: Record<string, SelectedLineRange | null>
}

export type TerminalHandoff = {
  titles: string[]
  preview?: PreviewCandidate
}

const MAX = 40

const store = {
  session: new Map<string, HandoffSession>(),
  terminal: new Map<string, TerminalHandoff>(),
}

const [terminalHandoffVersion, setTerminalHandoffVersion] = createSignal(0)

const touch = <K, V>(map: Map<K, V>, key: K, value: V) => {
  map.delete(key)
  map.set(key, value)
  while (map.size > MAX) {
    const first = map.keys().next().value
    if (first === undefined) return
    map.delete(first)
  }
}

export const setSessionHandoff = (key: string, patch: Partial<HandoffSession>) => {
  const prev = store.session.get(key) ?? { files: {} }
  touch(store.session, key, { ...prev, ...patch })
}

export const getSessionHandoff = (key: string) => store.session.get(key)

export const consumeSessionPromptHandoff = (key: string) => {
  const current = store.session.get(key)
  const promptParts = current?.promptParts
  if (!current || !promptParts) return

  const rest = { ...current, promptParts: undefined }
  if (!rest.prompt && Object.keys(rest.files).length === 0) {
    store.session.delete(key)
    return promptParts
  }
  store.session.set(key, rest)
  return promptParts
}

export const setTerminalHandoff = (key: string, value: string[] | TerminalHandoff) => {
  touch(store.terminal, key, Array.isArray(value) ? { titles: value } : value)
  setTerminalHandoffVersion((version) => version + 1)
}

export const getTerminalHandoff = (key: string) => {
  terminalHandoffVersion()
  return store.terminal.get(key)
}
